import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as CloseButton } from '../../assets/icons/close-box.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { changeDateFormat } from '../../shared-functions/DataTime';

interface CustomDateRangeProperties {
  inputLabel?: string;
  value: string[];
  name: string;
  placeholder?: string;
  onChangeValue: (name: string, value: string[]) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  validFromDate: string;
  validToDate: string;
  error?: boolean;
  errorMessage?: string;
}

/**
 * Custom Multi Date Picker component
 * @param {CustomDateRangeProperties} properties - Component properties
 * @returns {any} The rendered CustomMultiDatePicker component
 */
function CustomMultiDatePicker(properties: CustomDateRangeProperties) {
  const {
    inputLabel,
    name,
    value,
    placeholder,
    onChangeValue,
    valueRequired,
    readOnly,
    validFromDate,
    validToDate,
    error,
    errorMessage,
  } = properties;

  const reference = useRef<HTMLDivElement>(null);
  const inputContainerReference = useRef<HTMLDivElement>(null);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [allDates, setAllDates] = useState<any[]>([]);
  const [style, setStyle] = useState<any>({});

  const [open, setOpen] = useState<boolean>(false);

  /**
   * Recalculates and updates the calendar portal position relative to the input container
   */
  const updatePosition = useCallback(() => {
    if (inputContainerReference.current) {
      const rect = inputContainerReference.current.getBoundingClientRect();
      const pickerWidth = 310;
      const pickerHeight = 285;
      const margin = 8;
      const { innerHeight: viewportHeight, innerWidth: viewportWidth } = window;

      if (rect.bottom < 0 || rect.top > viewportHeight) {
        setOpen(false);
      } else {
        let top = rect.bottom + 4;
        if (
          rect.bottom + pickerHeight + margin > viewportHeight &&
          rect.top > pickerHeight + margin
        ) {
          top = rect.top - pickerHeight - 4;
        }

        let {left} = rect;
        if (rect.left + pickerWidth + margin > viewportWidth) {
          left = viewportWidth - pickerWidth - margin;
        }

        setStyle({
          position: 'fixed',
          top: `${top}px`,
          left: `${Math.max(margin, left)}px`,
          zIndex: 13000,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return () => {};
    }

    updatePosition();

    /**
     * Handles window scroll and resize events to keep calendar attached to input container
     */
    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open, updatePosition]);

  /**
   * Handle the Value change
   * @param {string} changeValue - The changeValue
   */
  const handleValueChange = (changeValue: string) => {
    if (validFromDate <= changeValue && validToDate >= changeValue) {
      if (value.includes(changeValue)) {
        onChangeValue(
          name,
          value.filter((date) => date !== changeValue),
        );
      } else {
        onChangeValue(name, [...value, changeValue]);
      }
    }
  };

  /**
   * Handle the Input click
   */
  const openCalenderView = () => {
    if (!readOnly) {
      setOpen(true);
      updatePosition();
    }
  };

  /**
   * Get all days for a given month and year
   * @param {number} month - The month index (0-11)
   * @param {number} year - The year
   */
  const getAllDays = useCallback((month: number, year: number) => {
    const date = new Date(year, month, 1);
    let days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    days = days.map((valueChange) => ({
      date: changeDateFormat(valueChange.toString()),
      number: valueChange.getDate(),
      day: valueChange.getDay(),
    }));
    if (days[0].day > 0) {
      const firstMonth = days[0].day;
      for (let index = 0; index < firstMonth; index += 1) {
        days = [{}, ...days];
      }
    }
    setAllDates(days);
  }, []);

  /**
   * Handle the month change function
   * @param {string} type - The type of change ('add' or 'sub')
   */
  const changeMonth = (type: string) => {
    let changeValue = 0;
    let changeYear = currentYear;
    if (type === 'add') {
      changeValue = currentMonth + 1;
      if (changeValue === months.length) {
        changeValue = 0;
        changeYear = currentYear + 1;
        setCurrentYear(currentYear + 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    } else {
      changeValue = currentMonth - 1;
      if (changeValue === -1) {
        changeValue = 11;
        changeYear = currentYear - 1;
        setCurrentYear(currentYear - 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    }
  };

  /**
   * Handle close the calendar overflow click function
   * @param {MouseEvent} event - The mouse event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (
      reference.current &&
      !reference.current.contains(event.target as Node) &&
      inputContainerReference.current &&
      !inputContainerReference.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  /**
   * Remove the date by index
   * @param {number} index - The index of date to remove
   */
  const removeDate = (index: number) => {
    const oldValues = [...value];
    if (index > -1) {
      oldValues.splice(index, 1);
    }
    onChangeValue(name, oldValues);
  };

  useEffect(() => {
    getAllDays(currentMonth, currentYear);
  }, [currentMonth, currentYear, getAllDays]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`custom-input-date-wrap ${error ? 'error' : ''}`}>
      {inputLabel && (
        <p className={`input-label ${error ? 'error' : ''}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-date-input">
        <div
          ref={inputContainerReference}
          className={`custom-input-container ${error ? 'error' : ''}`}
          onClick={openCalenderView}
          role="presentation">
          <div className="value-view">
            {value.length === 0 && <p>{placeholder}</p>}
            {value.map((date, index) => (
              <div key={date} className="item">
                {date}
                <div className="button" role="presentation" onClick={() => removeDate(index)}>
                  <CloseButton />
                </div>
              </div>
            ))}
          </div>

          <span>
            <Calender />
          </span>
        </div>
        {open &&
          ReactDOM.createPortal(
            <div className="calender-box" ref={reference} style={style}>
              <div className="calender-header">
                <div
                  className="calender-header-left"
                  onClick={() => changeMonth('sub')}
                  role="presentation">
                  <LeftArrow />
                </div>
                <div className="calender-header-center">
                  {months[currentMonth]} - {currentYear}
                </div>
                <div
                  className="calender-header-right"
                  onClick={() => changeMonth('add')}
                  role="presentation">
                  <LeftArrow />
                </div>
              </div>
              <div className="calender-body">
                <div className="calender-header-items">
                  <div className="header-item">SUN</div>
                  <div className="header-item">MON</div>
                  <div className="header-item">TUS</div>
                  <div className="header-item">WEN</div>
                  <div className="header-item">THU</div>
                  <div className="header-item">FRI</div>
                  <div className="header-item">SAT</div>
                </div>
                <div className="calender-body-items">
                  {allDates.map((dateDetail: any, dateIndex: number) =>
                    Object.keys(dateDetail).length === 0 ? (
                      <div key={`empty-${dateIndex}`} className="header-item" />
                    ) : (
                      <div
                        key={dateDetail.date}
                        className={`header-item ${validFromDate <= dateDetail.date && validToDate >= dateDetail.date ? 'allowed' : ''} ${value.includes(dateDetail.date) ? 'active' : ''}`}
                        onClick={() => handleValueChange(dateDetail.date)}
                        role="presentation">
                        {dateDetail.number}
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
      {error && errorMessage && <p className="text-danger">{errorMessage}</p>}
    </div>
  );
}

CustomMultiDatePicker.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  readOnly: false,
  error: false,
  errorMessage: '',
};

export default CustomMultiDatePicker;
